import React, {useCallback, useMemo} from 'react'
import {
    Box,
    Divider,
    Flex,
    Image,
    Link as NavLink
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '../link'
import {getUrl} from '../../utils/cloudinary-utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import FindStore from './find-store'
import {useSiteContext} from '../../contexts/site'
import {KS_MY_ACCOUNT_URL, KS_WISHLIST_URL} from '../../constants'
import {useGlobaleContext} from '../../contexts/globale'
import useTagManager from '../../hooks/use-tag-manager'
import {useWishList} from '../../hooks/use-wish-list'
import {scheduleIdleAnalytics} from '../../utils/common-utils'
import useNavigationHandler, {getNavigationProps} from '../../hooks/use-navigation-handler'

const UtilityBanner = () => {
    const {isYR} = useSiteContext()
    const {
        app: {commerceAPI}
    } = getConfig()

    const {siteTheme} = commerceAPI.storefront

    const themeJSON = useMemo(() => {
        try {
            return JSON.parse(siteTheme)
        } catch (e) {
            return {}
        }
    }, [siteTheme])

    const tagManager = useTagManager()
    const {countryISO} = useGlobaleContext()
    const {data: wishListData} = useWishList()
    const handleNavigation = useNavigationHandler()

    const trackHeaderInteraction = useCallback(
        (action) => {
            scheduleIdleAnalytics(() => {
                tagManager.headerInteraction(action)
            })
        },
        [tagManager]
    )

    const onKsLogoClick = useCallback(
        (event) => {
            handleNavigation(event, getNavigationProps('/'), () => {
                trackHeaderInteraction('kendra-scott')
            })
        },
        [handleNavigation, trackHeaderInteraction]
    )

    const onYrLogoClick = useCallback(
        (event) => {
            handleNavigation(event, getNavigationProps('/yellow-rose.html'), () => {
                trackHeaderInteraction('yellow-rose')
            })
        },
        [handleNavigation, trackHeaderInteraction]
    )

    return (
        <>
            <Box
                background={isYR ? 'yrUtilityGradient' : 'ksUtilityGradient'}
                height={{xxxs: '0px', base: '26px'}}
            ></Box>
            <Flex
                className="utility-banner-wrapper"
                height={{base: '40px', lg: '48px'}}
                justify="space-between"
                align="center"
                p={{base: '0 15px 0 5px', md: '0 1rem', lg: '0 60px'}}
                bg={
                    isYR
                        ? themeJSON['yr']?.brandBarBg || 'black.300'
                        : themeJSON['ks']?.brandBarBg || 'ksBrandGradient'
                }
                color="white"
                fontFamily="body"
                fontSize="12px"
                fontWeight="300"
            >
                {/* Left Side */}
                <Flex className="left-logos" gap="0px" alignItems="center">
                    <Box
                        className="brand-logo ks-logo"
                        height="27px"
                        display="flex"
                        justify="center"
                        align="center"
                        sx={{
                            svg: {
                                marginBottom: '2px',
                                paddingLeft: '14px'
                            }
                        }}
                        overflow="hidden"
                    >
                        <Link
                            to="/"
                            className="ks-link"
                            alignContent="center"
                            borderRadius="5px"
                            display="flex"
                            alignItems="center"
                            _hover={{bg: {lg: 'grayAlpha5'}}}
                            onClick={onKsLogoClick}
                        >
                            {isYR ? (
                                <Image
                                    src={getUrl('UIImages/images/white-ks-utility.svg')}
                                    className="ks-icon"
                                    width="118px"
                                    height="8px"
                                    alt="Kendra Scott Logo"
                                />
                            ) : (
                                <Image
                                    className="ks-icon"
                                    src={getUrl('UIImages/images/black-ks-utility.svg')}
                                    width="118px"
                                    height="8px"
                                    alt="Kendra Scott Logo"
                                />
                            )}
                        </Link>
                    </Box>
                    <Divider
                        orientation="vertical"
                        height="15px"
                        display={{base: 'block', lg: 'none'}}
                        borderColor={isYR ? 'white' : 'black'}
                        margin="0 4px"
                    />
                    <Box
                        className="brand-logo redirect-link yr-logo"
                        height="27px"
                        display="flex"
                        justify="center"
                        align="center"
                        overflow="hidden"
                    >
                        <Link
                            to="/yellow-rose.html"
                            className="yr-link"
                            padding="0px 14px"
                            maxWidth={{base: '100px', sm: 'fit-content'}}
                            borderRadius="5px"
                            alignContent="center"
                            alignItems="center"
                            display="flex"
                            _hover={{bg: {lg: 'grayAlpha5'}}}
                            onClick={onYrLogoClick}
                        >
                            {isYR ? (
                                <Image
                                    className="yr-icon"
                                    src={getUrl('UIImages/images/white-yr-utility.svg')}
                                    width="76.62px"
                                    height="11.81px"
                                    alt="Yellow Rose Logo"
                                />
                            ) : (
                                <Image
                                    className="yr-icon"
                                    src={getUrl('UIImages/images/black-yr-utility.svg')}
                                    width="76.62px"
                                    height="11.81px"
                                    alt="Yellow Rose Logo"
                                />
                            )}
                        </Link>
                    </Box>
                </Flex>

                {/* Right Side */}
                <Flex
                    className="right-content"
                    align="center"
                    ml="auto"
                    display={{base: 'none', lg: 'block'}}
                >
                    <Flex className="utility-banner-svgs" align="center">
                        <Link
                            className="utility-link my-account-wrapper"
                            to={KS_MY_ACCOUNT_URL}
                            isExternal={true}
                            as={NavLink}
                            p="7.5px"
                            my="7.5px"
                            borderRadius="5px"
                            _hover={{bg: 'grayAlpha5'}}
                            onClick={() => {
                                trackHeaderInteraction('account')
                            }}
                        >
                            <Image
                                width="16px"
                                height="16px"
                                src={getUrl(
                                    isYR
                                        ? 'UIImages/images/user-circle-white.svg'
                                        : 'UIImages/images/user-circle-black.svg'
                                )}
                                alt="My Account"
                            />
                        </Link>

                        {countryISO === 'US' && (
                            <Link
                                className="utility-link wishlist-wrapper"
                                to={KS_WISHLIST_URL}
                                isExternal={true}
                                data-wishlist-count={wishListData?.customerProductListItems?.length}
                                as={NavLink}
                                p="7.5px"
                                my="7.5px"
                                borderRadius="5px"
                                _hover={{bg: 'grayAlpha5'}}
                                onClick={() => {
                                    trackHeaderInteraction('wishlist')
                                }}
                            >
                                <Image
                                    width="16px"
                                    height="16px"
                                    src={getUrl(
                                        isYR
                                            ? 'UIImages/images/heart-white.svg'
                                            : 'UIImages/images/heart-black.svg'
                                    )}
                                    alt="Wishlist"
                                />
                            </Link>
                        )}

                        <FindStore />

                        <Divider
                            orientation="vertical"
                            height="15px"
                            borderColor={isYR ? 'white' : 'black'}
                            marginLeft="5px"
                            marginRight="12px"
                        />
                        <Box className="accessibility-placeholder" opacity="0" />
                    </Flex>
                </Flex>
            </Flex>
        </>
    )
}

export default UtilityBanner
